import { PropTypes } from 'prop-types';
import styled, { css } from 'styled-components';

import EmptyDataPage from 'components/ui/EmptyDataPage';
import Segment from 'components/ui/Segment';
import emptyDataUrl from 'components/ui/svg/undraw_empty_xct9.svg';

import commonPropTypes from 'utils/commonPropTypes';

import * as svars from 'assets/style/variables';

import BeatingLoader from '../BeatingLoader';
import ListPagination from './Pagination';

export const sortItems = (items, { key, ascending }, accessor, sortType) =>
  items.sort((element1, element2) => {
    const element1Value = (accessor && accessor(element1)) || element1[key];
    const element2Value = (accessor && accessor(element2)) || element2[key];

    // If value is boolean do not consider `ascending` to have true value always first
    if (element1Value === true) return -1;
    if (element1Value === false) return 0;
    let sortValue = 0;
    if (element1Value !== null && element1Value !== undefined) {
      if (sortType === 'basic') {
        sortValue = element2Value - element1Value;
      } else if (element1Value?.localeCompare) {
        sortValue = element1Value.localeCompare(element2Value);
      } else if (typeof sortType === 'function') {
        sortValue = sortType(element1Value, element2Value);
      }
    } else if (element2Value !== null && element2Value !== undefined) {
      sortValue = 1;
    }

    return sortValue * (ascending ? 1 : -1);
  });

const MaybeClickableSegment = styled(Segment)`
  &&& {
    display: flex;
    flex-direction: column;
    ${({ compacted }) =>
      compacted
        ? `padding: ${svars.spaceNormal}; padding-right: 0; margin: ${svars.spaceSmaller} 3px;`
        : ''}
    ${({ clickable }) =>
      clickable
        ? css`
            ${svars.hoverClickableCss}
            ${svars.activeClickableCss}
          `
        : 'background: none'}
  }
`;

const ListContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  overflow: auto hidden;
  align-items: baseline;
  ${({ style }) =>
    style &&
    css`
      ${style}
    `}
`;

const ItemList = styled.div`
  height: 100%;
  min-width: 100%;
  overflow: clip auto;
  ${({ listStyle }) =>
    listStyle &&
    css`
      ${listStyle}
    `}
`;

export function ManagementListItems({
  filteredItems,
  columnSorted,
  emptySearchHeader,
  emptyListHeader,
  EmptyListContent,
  onResetAllFilters,
  style,
  HeaderComponent,
  onItemSort,
  compact,
  onRowClick,
  renderItemRow,
  rowFields,
  isRowActive,
  listStyle,
}) {
  return (
    (!filteredItems?.length && (
      <EmptyDataPage
        headerText={emptySearchHeader || emptyListHeader}
        illustrationUrl={emptyDataUrl}
        actionComponent={<EmptyListContent resetFilters={onResetAllFilters} />}
      />
    )) || (
      <ListContainer style={style}>
        <HeaderComponent onSort={onItemSort} sorted={columnSorted} />
        <ItemList listStyle={listStyle}>
          {filteredItems.map((item, index) => (
            <MaybeClickableSegment
              compacted={compact ? '1' : ''}
              // eslint-disable-next-line react/no-array-index-key
              key={`facet-${item.id}-${index}`}
              clickable={onRowClick ? '1' : ''}
              active={isRowActive?.(item) ? '1' : ''}
              onClick={onRowClick ? () => onRowClick(item) : null}
              data-testid={`bo-list-card-${item?.id}`}
            >
              {renderItemRow({ fields: rowFields, item, index })}
            </MaybeClickableSegment>
          ))}
        </ItemList>
      </ListContainer>
    )
  );
}

ManagementListItems.propTypes = {
  filteredItems: PropTypes.arrayOf(PropTypes.shape({})),
  columnSorted: PropTypes.shape({
    key: PropTypes.string,
    ascending: PropTypes.bool,
  }).isRequired,
  emptySearchHeader: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  emptyListHeader: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  EmptyListContent: PropTypes.oneOfType([PropTypes.func]).isRequired,
  onResetAllFilters: PropTypes.func.isRequired,
  style: commonPropTypes.style,
  HeaderComponent: PropTypes.func.isRequired,
  onItemSort: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  onRowClick: PropTypes.func,
  renderItemRow: PropTypes.func.isRequired,
  rowFields: PropTypes.arrayOf(commonPropTypes.uiFields).isRequired,
  isRowActive: PropTypes.func,
  listStyle: PropTypes.shape({}),
};

ManagementListItems.defaultProps = {
  filteredItems: [],
  emptySearchHeader: null,
  emptyListHeader: null,
  style: {},
  compact: false,
  onRowClick: null,
  isRowActive: null,
  listStyle: {},
};

export function ManagementPagination({
  fetchItems,
  totalPages,
  currentPage,
  loading,
  handlePageChange,
}) {
  if (loading) {
    return <BeatingLoader absolute />;
  }
  return fetchItems && totalPages ? (
    <ListPagination
      currentPage={currentPage}
      totalPages={totalPages}
      handlePageChange={handlePageChange}
      loading={loading}
    />
  ) : null;
}

ManagementPagination.propTypes = {
  fetchItems: PropTypes.func.isRequired,
  totalPages: PropTypes.number.isRequired,
  currentPage: PropTypes.number.isRequired,
  loading: PropTypes.bool.isRequired,
  handlePageChange: PropTypes.func.isRequired,
};

ManagementPagination.defaultProps = {};
